import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, AlertTriangle, Scale } from 'lucide-react';

const STATUS_COLORS = {
  potential_dispute: 'bg-amber-100 text-amber-700',
  internal_escalation: 'bg-orange-100 text-orange-700',
  negotiation_active: 'bg-blue-100 text-blue-700',
  formal_dispute: 'bg-red-100 text-red-700',
  referral_in_preparation: 'bg-purple-100 text-purple-700',
  forum_active: 'bg-indigo-100 text-indigo-700',
  awaiting_decision: 'bg-cyan-100 text-cyan-700',
};

const RISK_COLORS = { low: 'bg-green-100 text-green-700', medium: 'bg-amber-100 text-amber-700', high: 'bg-red-100 text-red-700', critical: 'bg-red-600 text-white' };

export default function PotentialDisputes({ projectId, settings, onOpenMatter, onCreateNew, statusFilter }) {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFormal = statusFilter === 'formal';
  const title = isFormal ? 'Formal Disputes' : 'Potential Disputes';

  useEffect(() => {
    (async () => {
      try {
        const res = await api.disputes.getRegister(projectId, {});
        const all = res.data.items || [];
        const formalStatuses = ['formal_dispute', 'referral_in_preparation', 'forum_active', 'awaiting_decision', 'decision_issued', 'compliance_pending'];
        const filtered = isFormal
          ? all.filter(m => formalStatuses.includes(m.status))
          : all.filter(m => m.status === 'potential_dispute' || m.status === 'internal_escalation' || m.status === 'negotiation_active');
        setMatters(filtered);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId, isFormal]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const Icon = isFormal ? Scale : AlertTriangle;

  return (
    <div className="space-y-4" data-testid={isFormal ? "formal-disputes" : "potential-disputes"}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          {title}
          <span className="ml-2 text-sm font-normal text-slate-400">({matters.length})</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={onCreateNew}>
          <Plus className="h-4 w-4" /> New Matter
        </Button>
      </div>

      {matters.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Icon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-medium text-slate-500">No {title.toLowerCase()}</p>
          <p className="text-sm text-slate-400 mt-1">Create a new matter to begin tracking</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {matters.map(m => (
            <Card key={m.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => onOpenMatter(m.id)} data-testid={`matter-${m.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-slate-400">{m.matter_ref}</span>
                      <Badge className={`text-[10px] ${STATUS_COLORS[m.status] || 'bg-slate-100'}`}>{m.status?.replace(/_/g, ' ')}</Badge>
                      <Badge className={`text-[10px] ${RISK_COLORS[m.risk_rating] || 'bg-slate-100'}`}>{m.risk_rating}</Badge>
                    </div>
                    <h3 className="text-sm font-semibold text-slate-800">{m.title}</h3>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      {m.counterparty && <span>vs {m.counterparty}</span>}
                      {m.originating_module && <span>From: {m.originating_module.replace(/_/g, ' ')}</span>}
                      {m.amount_in_dispute > 0 && <span className="font-mono">{new Intl.NumberFormat('en-ZA', { style: 'currency', currency: m.currency || 'ZAR', maximumFractionDigits: 0 }).format(m.amount_in_dispute)}</span>}
                      {m.time_in_dispute_days > 0 && <span>{m.time_in_dispute_days} days</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-500">{m.issue_count || 0} issues</p>
                    {m.next_deadline_date && <p className="text-xs text-slate-400 mt-0.5">Deadline: {m.next_deadline_date}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
